import React, { useMemo } from 'react';
import {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import TabNavigationStepper from '../../../../Components/Stepper/TabNavigationStepper/TabNavigationStepper';
import WorkplaceWorkersListWrapper from '../WorkplaceWorkers/WorkplaceWorkersList/WorkplaceWorkersListWrapper';
import ManagerWorkplaceWrapper from '../WorkplaceManager/ManagerWorkplaceWrapper/ManagerWorkplaceWrapper';
import ManagerWrapper from '../ManagerWrapper/ManagerWrapper';
import ManagerFinance from '../ManagerWrapper/Finance/ManagerFinance/ManagerFinance';
import InvoicingWrapper from '../../../Finance/Components/Invoicing/InvoicingWrapper/InvoicingWrapper';
import InsuranceReportManager from '../../../Insurance/Component/InsuranceReportManager/InsuranceReportManager';
import { useManagerContext } from '../../Services/ManagerContext';
import { isAllowed } from '../../../../Services/acl.service';

interface Props {
	isSuperAdmin: boolean;
}

const ManagerNavigation: React.FC<Props> = ({ isSuperAdmin }) => {
	const { setHealthPlanCode } = useManagerContext();

	const canManageDoctors = isAllowed('manager_doctors');
	const canManageWorkplace = isAllowed('manager_workplace');
	const canManageFinance = isAllowed('manager_finance');
	const canManageInvoicing = isAllowed('manager_invoicing');
	const canManageInsuranceOperation = isAllowed('insuranceOperation');

	const stepMeta = useMemo(() => {
		const temp: { name: string; key: string }[] = [];

		if (canManageDoctors) {
			temp.push({ name: 'base.workers', key: 'workers' });
		}

		if (canManageWorkplace) {
			temp.push({
				name: 'base.shared.editWorkplace',
				key: 'workplacesList',
			});
		}

		if (canManageWorkplace) {
			temp.push({
				name: 'base.shared.workplace',
				key: 'workplace',
			});
		}

		if (canManageFinance) {
			temp.push({ name: 'base.finance', key: 'finance' });
		}

		if (canManageInvoicing) {
			temp.push({ name: 'base.invoicing', key: 'invoices' });
		}

		if (canManageInsuranceOperation) {
			temp.push({
				name: 'base.reportingForInsuranceCompany',
				key: 'insuranceReport',
			});
		}

		return temp;
	}, [
		canManageDoctors,
		canManageWorkplace,
		canManageFinance,
		canManageInvoicing,
		canManageInsuranceOperation,
	]);

	const componentMap: Record<
		string,
		(params: NavigationStepperComponentParams) => JSX.Element
	> = {
		workers: () => (
			<WorkplaceWorkersListWrapper key='workers' showAllWorkplaces={true} />
		),
		workplacesList: () => <ManagerWorkplaceWrapper key='workplacesList' />,
		workplace: () => <ManagerWrapper key='workplace' />,
		finance: () => <ManagerFinance key='finance' />,
		invoices: () => (
			<InvoicingWrapper key='invoices' isSuperAdmin={isSuperAdmin} />
		),
		insuranceReport: () => <InsuranceReportManager key='insuranceReport' />,
	};

	const steps: NavigationStepperItemType[] = stepMeta.map(({ name, key }) => ({
		name,
		key,
		component: componentMap[key],
	}));

	const handleOnStepChange = (key: string) => {
		if (key !== 'caseTypes') {
			setHealthPlanCode(undefined);
		}
	};

	return (
		<TabNavigationStepper
			steps={steps}
			initialStepPath={'workers'}
			fallbackStep={'workers'}
			onStepChange={handleOnStepChange}
			canSkip
		/>
	);
};

export default ManagerNavigation;
